import { ReactElement } from 'react'

import { Alert } from 'antd'
import { useSelector } from 'react-redux'

import { IconButton } from 'components/Buttons'
import { Flex } from 'components/core'
import { CloseIcon } from 'components/Icons'
import { date } from 'configuration/dayjs'
import { dismissUserSurvey } from 'features/App/appService'
import { isUnset } from 'helpers/utilities'
import { selectDomainValue as selectAuthDomainValue } from 'selectors/auth'
import { selectDomainValue } from 'selectors/ui'
import { RootReduxState } from 'types'

import * as styles from './styles.scss'

const DISMISSAL_LENGTH_DAYS = 60 // Length of time the banner will be dismissed in days

function UserFeedbackBanner(): ReactElement | null {
    const lastRead = useSelector((state: RootReduxState) =>
        selectDomainValue(state, ['app', 'userSurveyLastRead'])
    )
    const dateJoined = useSelector((state: RootReduxState) =>
        selectAuthDomainValue(state, ['date_joined'])
    )
    const today = date()

    const shouldDisplayBanner = (): boolean => {
        const nextShowDate =
            lastRead && date(lastRead).add(DISMISSAL_LENGTH_DAYS, 'days')
        const firstShowDate =
            dateJoined && date(dateJoined).add(DISMISSAL_LENGTH_DAYS, 'days')

        const recentlyDismissed = nextShowDate && today.isBefore(nextShowDate)
        const newUser = firstShowDate && today.isBefore(firstShowDate)

        return !newUser && (isUnset(lastRead) || !recentlyDismissed)
    }

    const handleDismiss = (): void => {
        dismissUserSurvey(today.toISOString())
    }

    const messageBody = (
        <Flex justifyContent="space-between">
            <Flex>
                Your feedback is important to us. Please take a moment to share
                your experience.
                <a
                    id="fs-survey-link" // fs-survey-link is for Full Story to reference
                    target="_blank"
                    rel="noopener noreferrer"
                    href="https://downstreamimpact.typeform.com/to/Dhq8Kb"
                    onClick={handleDismiss}
                    style={{ marginLeft: 4 }}
                >
                    Take the Survey
                </a>
            </Flex>
            <IconButton
                icon={<CloseIcon />}
                onClick={handleDismiss}
                size="small"
                type="link"
            />
        </Flex>
    )

    return shouldDisplayBanner() ? (
        <Alert
            banner
            className={styles['info-alert']}
            message={messageBody}
            showIcon={false}
            type="info"
        />
    ) : null
}

export default UserFeedbackBanner
